import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getAirportName, getAirportCity } from "@/utils/airports";
import {
  ArrowLeft, Plane, Mail, Phone,
  Users, DollarSign, Trash2, FileText, UserPlus, Pencil, CheckCircle, Copy
} from "lucide-react";
import FlightSearchPanel from "@/components/FlightSearchPanel";
import FareAlertsPanel from "@/components/FareAlertsPanel";
import PageLoader from "../components/PageLoader";
import EmptyState from "../components/EmptyState";
import StatusBadge from "../components/StatusBadge";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Helpers ──────────────────────────────────────────────────────────────

function formatTime(isoStr) {
  if (!isoStr) return "--:--";
  return new Date(isoStr).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

function formatDate(isoStr) {
  if (!isoStr) return "";
  return new Date(isoStr).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
}

function getDealSegments(deal) {
  if (deal.segments && deal.segments.length > 0) return deal.segments;
  return [{
    departure_airport: deal.departure_airport,
    arrival_airport: deal.arrival_airport,
    departure_time: deal.departure_time,
    arrival_time: deal.arrival_time,
    flight_number: deal.flight_number,
    operated_by: deal.airline
  }];
}

const buildQuoteItinerary = (offer, type) => {
  if (type === "duffel") {
    return (offer.slices || []).flatMap(s => s.segments || []).map(seg => ({
      departure_airport: seg.origin?.iata_code || "",
      arrival_airport: seg.destination?.iata_code || "",
      departure_date: seg.departing_at?.split("T")[0] || "",
      departure_time: formatTime(seg.departing_at),
      arrival_time: formatTime(seg.arriving_at),
      flight_number: `${seg.marketing_carrier?.iata_code || ""}${seg.marketing_carrier_flight_number || ""}`,
      airline: seg.operating_carrier?.name || seg.marketing_carrier?.name || offer.owner?.name || "",
    }));
  }
  return getDealSegments(offer).map(seg => ({
    departure_airport: seg.departure_airport || "",
    arrival_airport: seg.arrival_airport || "",
    departure_date: seg.departure_time ? new Date(seg.departure_time).toISOString().split("T")[0] : "",
    departure_time: seg.departure_time ? formatTime(seg.departure_time) : "",
    arrival_time: seg.arrival_time ? formatTime(seg.arrival_time) : "",
    flight_number: seg.flight_number || "",
    airline: seg.operated_by || offer.airline || "",
  }));
};

// ── Component ────────────────────────────────────────────────────────────

export default function RequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();

  const [request, setRequest]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [duffelOffers, setDuffelOffers] = useState([]);
  const [duffelLoading, setDuffelLoading] = useState(false);
  const [manualDeals, setManualDeals] = useState([]);

  const [showManualDealDialog, setShowManualDealDialog] = useState(false);
  const [editDealDialog, setEditDealDialog]             = useState(false);
  const [editingDeal, setEditingDeal]                   = useState(null);
  const [editDealForm, setEditDealForm]                 = useState(null);
  const [showBookingDialog, setShowBookingDialog]       = useState(false);
  const [selectedOffer, setSelectedOffer]               = useState(null);
  const [bookingForm, setBookingForm]                   = useState({ base_price: "", selling_price: "", passengers: [] });

  const [showEditRequest, setShowEditRequest] = useState(false);
  const [editForm, setEditForm]               = useState(null);
  const [savingEdit, setSavingEdit]           = useState(false);

  const [showQuoteDialog, setShowQuoteDialog] = useState(false);
  const [quoteOffer, setQuoteOffer]           = useState(null);
  const [quoteForm, setQuoteForm]             = useState({
    selling_price: "", base_price: "", notes: "", baggage_allowance: "",
    dates_searched: "", airports_searched: "", hours_spent: "", include_research_summary: true
  });
  const [sendToEmail, setSendToEmail]   = useState("");
  const [sendingQuote, setSendingQuote] = useState(false);

  const emptySegment = { departure_airport: "", arrival_airport: "", departure_time: "", arrival_time: "", flight_number: "", operated_by: "" };
  const [manualDealForm, setManualDealForm] = useState({
    airline: "", segments: [{ ...emptySegment }], price: "", cabin_class: "economy", baggage_included: "23kg", notes: ""
  });

  useEffect(() => { fetchRequestData(); }, [id]);

  const fetchRequestData = async () => {
    try {
      const [requestRes, dealsRes] = await Promise.all([
        axios.get(`${API}/flight-requests/${id}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/manual-deals`, { headers: getAuthHeaders(), params: { request_id: id } })
      ]);
      setRequest(requestRes.data);
      setManualDeals(dealsRes.data);
      const reqData = requestRes.data;
      if (reqData.travel_class) {
        setManualDealForm(prev => ({ ...prev, cabin_class: reqData.travel_class }));
      }
    } catch (error) { toast.error("Failed to load request data"); navigate("/requests"); }
    finally { setLoading(false); }
  };

  const handleDuffelSearch = async () => {
    setDuffelLoading(true);
    try {
      const response = await axios.post(`${API}/flights/search?request_id=${id}`, {}, { headers: getAuthHeaders() });
      if (response.data.offers?.length > 0) {
        setDuffelOffers(response.data.offers);
        toast.success(`Found ${response.data.offers.length} Duffel offers!`);
      } else {
        toast.info("No Duffel flights found for this route.");
      }
      fetchRequestData();
    } catch (error) { toast.error("Duffel search failed. Try adding manual deals."); }
    finally { setDuffelLoading(false); }
  };

  const handleOpenQuote = (offer, type) => {
    const price = type === "duffel" ? parseFloat(offer.total_amount) : parseFloat(offer.price);
    const itinerary = buildQuoteItinerary(offer, type);
    setQuoteOffer({ ...offer, type, itinerary });
    setQuoteForm({
      base_price: price.toFixed(2), selling_price: "", notes: "",
      baggage_allowance: "1x 23kg checked + 1x 7kg cabin",
      dates_searched: "", airports_searched: "", hours_spent: "", include_research_summary: true,
    });
    setSendToEmail(request?.email || "");
    setShowQuoteDialog(true);
  };

  const handleSendQuote = async () => {
    if (!quoteForm.selling_price) { toast.error("Enter a selling price"); return; }
    setSendingQuote(true);
    try {
      const addDays = (dateStr, n) => {
        const d = new Date(dateStr); d.setDate(d.getDate() + n);
        return d.toISOString().split("T")[0];
      };
      const itinerary = quoteOffer.itinerary || [];
      const segs = itinerary.map(s => {
        const depISO = s.departure_date && s.departure_time ? `${s.departure_date}T${s.departure_time}` : "";
        let arrISO = "";
        if (s.departure_date && s.arrival_time) {
          arrISO = `${s.departure_date}T${s.arrival_time}`;
          if (depISO && arrISO <= depISO) arrISO = `${addDays(s.departure_date, 1)}T${s.arrival_time}`;
        }
        return { departure_airport: s.departure_airport, arrival_airport: s.arrival_airport, departure_date: s.departure_date, departure_time: depISO, arrival_time: arrISO, flight_number: s.flight_number || "", airline: s.airline || "" };
      });
      for (let i = 1; i < segs.length; i++) {
        const prevArr = segs[i - 1].arrival_time;
        const curDep  = segs[i].departure_time;
        if (prevArr && curDep && curDep <= prevArr) {
          const prevDate = prevArr.split("T")[0];
          const curTime  = segs[i].departure_time.split("T")[1];
          const arrTime  = segs[i].arrival_time ? segs[i].arrival_time.split("T")[1] : null;
          const newDepDate = addDays(prevDate, 1);
          segs[i].departure_time = `${newDepDate}T${curTime}`;
          segs[i].departure_date = newDepDate;
          if (arrTime) {
            const arrDate = segs[i].arrival_time.split("T")[0];
            const depDate = segs[i - 1].arrival_time.split("T")[0];
            const dayDiff = (new Date(arrDate) - new Date(depDate)) / 86400000;
            segs[i].arrival_time = `${addDays(newDepDate, Math.max(0, dayDiff))}T${arrTime}`;
          }
        }
      }
      const airline = quoteOffer.type === "duffel" ? (quoteOffer.owner?.name || "") : quoteOffer.airline;
      const description = itinerary.map(s => `${s.departure_airport} → ${s.arrival_airport}${s.departure_date ? ` (${s.departure_date})` : ""}`).join(", ");
      const res = await axios.post(`${API}/quotes`, {
        request_id: id, customer_name: `${request.first_name} ${request.last_name}`,
        customer_email: sendToEmail || request.email, customer_phone: request.phone,
        flight_description: description, segments: segs, airline,
        cabin_class: request.travel_class, baggage: request.baggage,
        base_price: parseFloat(quoteForm.base_price) || 0, selling_price: parseFloat(quoteForm.selling_price),
        notes: quoteForm.notes, baggage_allowance: quoteForm.baggage_allowance || "",
        dates_searched: quoteForm.dates_searched ? parseInt(quoteForm.dates_searched) : null,
        airports_searched: quoteForm.airports_searched ? parseInt(quoteForm.airports_searched) : null,
        hours_spent: quoteForm.hours_spent ? parseFloat(quoteForm.hours_spent) : null,
        include_research_summary: quoteForm.include_research_summary,
      }, { headers: getAuthHeaders(), timeout: 60000 });
      if (res.data?.email_sent === false) {
        toast.warning(`Quote saved, but email failed: ${res.data?.email_error || "Check Resend settings"}`);
      } else {
        toast.success(`Quote sent to ${sendToEmail || request.email}!`);
      }
      setShowQuoteDialog(false);
    } catch (e) {
      const data = e.response?.data;
      let msg = "Failed to send quote";
      if (data?.detail) {
        msg = Array.isArray(data.detail) ? data.detail.map(d => `${d.loc?.join(".")}: ${d.msg}`).join(" | ") : String(data.detail);
      } else if (e.code === "ECONNABORTED" || e.message?.includes("timeout")) {
        msg = "Request timed out — the quote may still be processing.";
      }
      toast.error(msg);
    } finally { setSendingQuote(false); }
  };

  const handleSaveToContacts = async () => {
    try {
      const res = await axios.post(`${API}/contacts/import-from-request/${id}`, {}, { headers: getAuthHeaders() });
      if (res.data.existing) toast.info(`Contact already exists for ${request.email}`);
      else toast.success(`Contact saved: ${request.first_name} ${request.last_name}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save contact"); }
  };

  const openEditRequest = () => {
    setEditForm({
      travel_type: request.travel_type, travel_class: request.travel_class,
      baggage: request.baggage, passengers: { ...request.passengers },
      segments: request.segments.map(s => ({ ...s })),
    });
    setShowEditRequest(true);
  };

  const handleDuplicateRequest = async () => {
    try {
      const res = await axios.post(`${API}/flight-requests/${id}/duplicate`, {}, { headers: getAuthHeaders() });
      toast.success(`Duplicated as ${res.data.request_number}`);
      navigate(`/requests/${res.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to duplicate request");
    }
  };

  const handleSaveRequest = async () => {
    setSavingEdit(true);
    try {
      await axios.patch(`${API}/flight-requests/${id}`, editForm, { headers: getAuthHeaders() });
      toast.success("Request updated!"); setShowEditRequest(false); fetchRequestData();
    } catch (e) { toast.error("Failed to save changes"); }
    finally { setSavingEdit(false); }
  };

  const handleManualSegmentChange = (index, field, value) => {
    setManualDealForm(prev => { const s = [...prev.segments]; s[index] = { ...s[index], [field]: value }; return { ...prev, segments: s }; });
  };
  const addManualSegment = () => {
    if (manualDealForm.segments.length < 6) setManualDealForm(prev => ({ ...prev, segments: [...prev.segments, { ...emptySegment }] }));
  };
  const removeManualSegment = (i) => {
    if (manualDealForm.segments.length > 1) setManualDealForm(prev => ({ ...prev, segments: prev.segments.filter((_, j) => j !== i) }));
  };

  const handleAddManualDeal = async () => {
    try {
      const firstSeg = manualDealForm.segments[0] || {};
      await axios.post(`${API}/manual-deals`, {
        request_id: id, airline: manualDealForm.airline, segments: manualDealForm.segments,
        flight_number: firstSeg.flight_number || "", departure_airport: firstSeg.departure_airport || "",
        arrival_airport: firstSeg.arrival_airport || "", departure_time: firstSeg.departure_time || "",
        arrival_time: firstSeg.arrival_time || "", price: parseFloat(manualDealForm.price),
        cabin_class: manualDealForm.cabin_class, baggage_included: manualDealForm.baggage_included, notes: manualDealForm.notes
      }, { headers: getAuthHeaders() });
      toast.success("Manual deal added!"); setShowManualDealDialog(false); fetchRequestData();
    } catch (error) { toast.error("Failed to add manual deal"); }
  };

  const handleDeleteDeal = async (dealId) => {
    try {
      await axios.delete(`${API}/manual-deals/${dealId}`, { headers: getAuthHeaders() });
      setManualDeals(prev => prev.filter(d => d.id !== dealId));
      toast.success("Deal deleted");
    } catch (e) { toast.error("Failed to delete deal"); }
  };

  const handleOpenEditDeal = (deal) => {
    setEditingDeal(deal);
    setEditDealForm({
      airline: deal.airline || "",
      segments: (deal.segments?.length > 0 ? deal.segments : [{
        departure_airport: deal.departure_airport || "",
        arrival_airport: deal.arrival_airport || "",
        departure_time: deal.departure_time || "",
        arrival_time: deal.arrival_time || "",
        flight_number: deal.flight_number || "",
        operated_by: "",
      }]).map(s => ({ ...s })),
      price: deal.price?.toString() || "",
      currency: deal.currency || "EUR",
      cabin_class: deal.cabin_class || "economy",
      baggage_included: deal.baggage_included || "",
      notes: deal.notes || "",
    });
    setEditDealDialog(true);
  };

  const handleSaveEditDeal = async () => {
    if (!editDealForm || !editingDeal) return;
    try {
      const firstSeg = editDealForm.segments[0] || {};
      const payload = {
        request_id: editingDeal.request_id,
        airline: editDealForm.airline,
        segments: editDealForm.segments,
        flight_number: firstSeg.flight_number || "",
        departure_airport: firstSeg.departure_airport || "",
        arrival_airport: firstSeg.arrival_airport || "",
        departure_time: firstSeg.departure_time || "",
        arrival_time: firstSeg.arrival_time || "",
        price: parseFloat(editDealForm.price),
        currency: editDealForm.currency || "EUR",
        cabin_class: editDealForm.cabin_class,
        baggage_included: editDealForm.baggage_included,
        notes: editDealForm.notes,
      };
      const res = await axios.put(`${API}/manual-deals/${editingDeal.id}`, payload, { headers: getAuthHeaders() });
      setManualDeals(prev => prev.map(d => d.id === editingDeal.id ? res.data : d));
      toast.success("Deal updated");
      setEditDealDialog(false);
      setEditingDeal(null);
    } catch (e) { toast.error("Failed to update deal"); }
  };

  const handleEditSegmentChange = (index, field, value) => {
    setEditDealForm(prev => { const s = [...prev.segments]; s[index] = { ...s[index], [field]: value }; return { ...prev, segments: s }; });
  };
  const addEditSegment = () => {
    if (editDealForm.segments.length < 6) setEditDealForm(prev => ({ ...prev, segments: [...prev.segments, { ...emptySegment }] }));
  };
  const removeEditSegment = (i) => {
    if (editDealForm.segments.length > 1) setEditDealForm(prev => ({ ...prev, segments: prev.segments.filter((_, j) => j !== i) }));
  };

  const buildPassengersFromRequest = () => {
    if (!request) return [{ title: "Mr", first_name: "", last_name: "", email: "", phone: "", date_of_birth: "", gender: "m", passenger_type: "adult" }];
    const pax = [];
    for (let i = 0; i < (request.passengers.adults || 1); i++) pax.push({ title: "Mr", first_name: "", last_name: "", email: i === 0 ? request.email : "", phone: i === 0 ? request.phone : "", date_of_birth: "", gender: "m", passenger_type: "adult" });
    for (let i = 0; i < (request.passengers.children || 0); i++) pax.push({ title: "Mstr", first_name: "", last_name: "", email: "", phone: "", date_of_birth: "", gender: "m", passenger_type: "child" });
    for (let i = 0; i < (request.passengers.infants || 0); i++) pax.push({ title: "Mstr", first_name: "", last_name: "", email: "", phone: "", date_of_birth: "", gender: "m", passenger_type: "infant" });
    return pax;
  };

  const handleSelectOffer = (offer, type) => {
    setSelectedOffer({ ...offer, type });
    setBookingForm({ base_price: type === "duffel" ? offer.total_amount : offer.price.toString(), selling_price: "", passengers: buildPassengersFromRequest() });
    setShowBookingDialog(true);
  };

  const handleCreateBooking = async () => {
    try {
      const passengers = bookingForm.passengers.map(p => ({
        title: p.title, first_name: p.first_name, last_name: p.last_name,
        email: p.email || request?.email || "", phone: p.phone || request?.phone || "",
        date_of_birth: p.date_of_birth || "", gender: p.gender || (["Mrs","Ms","Miss"].includes(p.title) ? "f" : "m")
      }));
      let offer_data = null;
      if (selectedOffer.type === "duffel") {
        const { getAirportName: gan } = await import("@/utils/airports");
        offer_data = {
          type: "duffel", owner: selectedOffer.owner, total_amount: selectedOffer.total_amount, total_currency: selectedOffer.total_currency,
          slices: selectedOffer.slices?.map(slice => ({
            segments: slice.segments?.map(seg => ({
              origin_iata: seg.origin?.iata_code, origin_name: gan(seg.origin?.iata_code),
              destination_iata: seg.destination?.iata_code, destination_name: gan(seg.destination?.iata_code),
              departing_at: seg.departing_at, arriving_at: seg.arriving_at, duration: seg.duration,
              flight_number: `${seg.marketing_carrier?.iata_code || ""}${seg.marketing_carrier_flight_number || ""}`,
              operating_carrier: seg.operating_carrier?.name || seg.marketing_carrier?.name || "",
              cabin_class: seg.passengers?.[0]?.cabin_class || "economy", baggages: seg.passengers?.[0]?.baggages || []
            }))
          }))
        };
      } else {
        const dealSegments = getDealSegments(selectedOffer);
        offer_data = {
          type: "manual", airline: selectedOffer.airline,
          segments: dealSegments.map(seg => ({
            departure_airport: seg.departure_airport, arrival_airport: seg.arrival_airport,
            departure_time: seg.departure_time, arrival_time: seg.arrival_time,
            flight_number: seg.flight_number || "", operated_by: seg.operated_by || selectedOffer.airline
          })),
          price: selectedOffer.price, cabin_class: selectedOffer.cabin_class,
          baggage_included: selectedOffer.baggage_included, notes: selectedOffer.notes
        };
      }
      await axios.post(`${API}/bookings`, {
        request_id: id, offer_id: selectedOffer.id, offer_type: selectedOffer.type, passengers,
        base_price: parseFloat(bookingForm.base_price), selling_price: parseFloat(bookingForm.selling_price),
        currency: "EUR", offer_data
      }, { headers: getAuthHeaders() });
      toast.success("Booking created successfully!"); navigate("/bookings");
    } catch (error) { toast.error("Failed to create booking"); }
  };

  const profit = bookingForm.selling_price && bookingForm.base_price
    ? (parseFloat(bookingForm.selling_price) - parseFloat(bookingForm.base_price)).toFixed(2) : "0.00";

  if (loading) return (
    <DashboardLayout>
      <PageLoader message="Loading request…" />
    </DashboardLayout>
  );
  if (!request) return (
    <DashboardLayout>
      <EmptyState title="Request not found" description="This request could not be loaded." />
    </DashboardLayout>
  );

  // Format a short date like "19 Jun" from "2026-06-19"
  const fmtSegDate = (d) => {
    if (!d) return "";
    const dt = new Date(d + "T00:00:00");
    return dt.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  };

  const paxSummary = [
    request.passengers.adults  > 0 ? `${request.passengers.adults} ${request.passengers.adults   === 1 ? "Adult"  : "Adults"}`   : null,
    request.passengers.children > 0 ? `${request.passengers.children} ${request.passengers.children === 1 ? "Child"  : "Children"}` : null,
    request.passengers.infants  > 0 ? `${request.passengers.infants} ${request.passengers.infants  === 1 ? "Infant" : "Infants"}`  : null,
  ].filter(Boolean).join(", ");

  return (
    <DashboardLayout>
      <div className="flex flex-col h-screen overflow-hidden overflow-x-hidden" data-testid="request-detail-page">

        {/* ══ TOP INFO BAR ══ */}
        <div className="shrink-0 bg-white" style={{ borderBottom: "2px solid #c7d2fe" }} data-testid="info-bar">
          {/* Mobile: stacks vertically. md+: single horizontal row. */}
        <div className="flex flex-col md:flex-row md:items-stretch">

          {/* Row 1 on mobile: back button + customer name side-by-side */}
          <div className="flex items-stretch">
            {/* Back button */}
            <div className="flex items-center px-3 border-r border-slate-100 shrink-0">
              <Button variant="ghost" size="icon" onClick={() => navigate("/requests")} data-testid="back-btn" className="h-8 w-8 shrink-0">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </div>

            {/* Purple left accent + customer name block */}
            <div className="flex items-center gap-3 px-4 py-3 flex-1 min-w-0 md:border-r border-slate-100" style={{ borderLeft: "3px solid #6366f1" }}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <span className="text-base font-bold text-slate-900 leading-tight">{request.first_name} {request.last_name}</span>
                  <StatusBadge status={request.status} />
                </div>
                <div className="flex items-center gap-x-3 gap-y-0.5 text-[13px] text-slate-500 flex-wrap">
                  <span className="flex items-center gap-1 min-w-0"><Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" /><span className="truncate">{request.email}</span></span>
                  {request.phone && <span className="flex items-center gap-1 shrink-0"><Phone className="w-3.5 h-3.5 text-slate-400" />{request.phone}</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Trip details + route */}
          <div className="flex flex-col justify-center gap-1.5 px-4 py-3 flex-1 min-w-0 border-t md:border-t-0">
            {/* Trip pills */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {[
                request.travel_type.replace("_", " "),
                request.travel_class,
                paxSummary,
                request.baggage.replace("_", " "),
              ].map((label, i) => (
                <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-100 capitalize">{label}</span>
              ))}
              {(request.departure_flexibility > 0 || request.return_flexibility > 0) && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-100">flex dates</span>
              )}
            </div>
            {/* Route */}
            <div className="flex items-center gap-2 flex-wrap">
              {request.segments.map((seg, i) => (
                <span key={i} className="flex items-center gap-1 text-[13px]">
                  {i > 0 && <span className="text-slate-300 mx-1">·</span>}
                  <span className="font-mono font-bold text-slate-800">{seg.departure_airport}</span>
                  <span className="text-slate-400 text-xs">→</span>
                  <span className="font-mono font-bold text-slate-800">{seg.arrival_airport}</span>
                  <span className="text-slate-500 text-xs ml-0.5">{fmtSegDate(seg.departure_date)}</span>
                </span>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5 px-4 py-3 shrink-0 flex-wrap border-t md:border-t-0 md:border-l border-slate-100">
            {request.customer_notes && (
              <span className="text-[11px] text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 max-w-[160px] truncate" title={request.customer_notes}>
                📝 {request.customer_notes}
              </span>
            )}
            <Button variant="ghost" size="sm" onClick={handleSaveToContacts} className="text-indigo-600 hover:bg-indigo-50 h-8 px-3 text-xs">
              <UserPlus className="w-3.5 h-3.5 mr-1" />Save
            </Button>
            <Button variant="outline" size="sm" onClick={openEditRequest} className="border-indigo-200 text-indigo-700 hover:bg-indigo-50 h-8 px-3 text-xs">
              <Pencil className="w-3.5 h-3.5 mr-1" />Edit
            </Button>
            <Button variant="outline" size="sm" onClick={handleDuplicateRequest} className="border-slate-200 text-slate-600 hover:bg-slate-50 h-8 px-3 text-xs">
              <Copy className="w-3.5 h-3.5 mr-1" />Duplicate
            </Button>
          </div>

        </div>
        </div>

        {/* ══ FARE MONITOR ALERTS ══ */}
        <FareAlertsPanel requestId={id} />

        {/* ══ FULL-WIDTH SEARCH PANEL ══ */}
        <main className="flex-1 overflow-y-auto">
          <FlightSearchPanel
            request={request}
            duffelOffers={duffelOffers}
            duffelLoading={duffelLoading}
            onDuffelSearch={handleDuffelSearch}
            manualDeals={manualDeals}
            onQuote={handleOpenQuote}
            onSelectOffer={handleSelectOffer}
            onDeleteDeal={handleDeleteDeal}
            onEditDeal={handleOpenEditDeal}
            onAddManualDeal={() => setShowManualDealDialog(true)}
            onNewDeal={(flight) => {
              const toDT = (t) => t ? t.replace(" ", "T") : "";
              setManualDealForm(prev => ({
                ...prev,
                airline: flight.airline || "",
                segments: (flight.legs?.length > 0 ? flight.legs : [flight]).map(leg => ({
                  departure_airport: leg.departure_airport || "",
                  arrival_airport: leg.arrival_airport || "",
                  departure_time: toDT(leg.departure_time),
                  arrival_time: toDT(leg.arrival_time),
                  flight_number: (leg.flight_number || "").replace(/\s+/g, ""),
                  operated_by: leg.airline || flight.airline || ""
                }))
              }));
              setShowManualDealDialog(true);
            }}
            onAddLeg={(flight) => {
              const toDT = (t) => t ? t.replace(" ", "T") : "";
              const newLegs = (flight.legs?.length > 0 ? flight.legs : [flight]).map(leg => ({
                departure_airport: leg.departure_airport || "",
                arrival_airport: leg.arrival_airport || "",
                departure_time: toDT(leg.departure_time),
                arrival_time: toDT(leg.arrival_time),
                flight_number: (leg.flight_number || "").replace(/\s+/g, ""),
                operated_by: leg.airline || flight.airline || ""
              }));
              setManualDealForm(prev => {
                // If dialog is not open yet (fresh add), replace segments; otherwise append
                const currentSegs = prev.segments;
                const isEmpty = currentSegs.length === 1 && !currentSegs[0].departure_airport && !currentSegs[0].arrival_airport;
                return { ...prev, segments: (isEmpty && !showManualDealDialog) ? newLegs : [...currentSegs, ...newLegs] };
              });
              if (!showManualDealDialog) setShowManualDealDialog(true);
              toast.success(`Added ${newLegs.length} leg${newLegs.length > 1 ? "s" : ""} to deal`);
            }}
          />
        </main>
      </div>

      {/* ══════ Add Manual Deal Dialog ══════ */}
      <Dialog open={showManualDealDialog} onOpenChange={setShowManualDealDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add Manual Deal</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Airline</Label><Input value={manualDealForm.airline} onChange={(e) => setManualDealForm(p => ({ ...p, airline: e.target.value }))} placeholder="e.g., British Airways" /></div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Price (EUR)</Label><Input type="number" value={manualDealForm.price} onChange={(e) => setManualDealForm(p => ({ ...p, price: e.target.value }))} placeholder="0.00" /></div>
                <div><Label>Class</Label>
                  <Select value={manualDealForm.cabin_class} onValueChange={(v) => setManualDealForm(p => ({ ...p, cabin_class: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="economy">Economy</SelectItem><SelectItem value="business">Business</SelectItem><SelectItem value="first">First</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Baggage</Label><Input value={manualDealForm.baggage_included} onChange={(e) => setManualDealForm(p => ({ ...p, baggage_included: e.target.value }))} placeholder="23kg" /></div>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Flight Segments ({manualDealForm.segments.length})</Label>
                <Button variant="ghost" size="sm" onClick={addManualSegment} disabled={manualDealForm.segments.length >= 6} className="text-indigo-600">+ Add Segment</Button>
              </div>
              {manualDealForm.segments.map((seg, idx) => (
                <div key={idx} className="p-4 bg-indigo-50 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-indigo-700">Segment {idx + 1}</span>
                    {manualDealForm.segments.length > 1 && <Button variant="ghost" size="sm" onClick={() => removeManualSegment(idx)}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">From</Label><Input value={seg.departure_airport} onChange={(e) => handleManualSegmentChange(idx, "departure_airport", e.target.value.toUpperCase())} maxLength={3} className="uppercase font-mono" placeholder="LHR" /><p className="text-xs text-indigo-400 mt-0.5">{getAirportName(seg.departure_airport)}</p></div>
                    <div><Label className="text-xs">To</Label><Input value={seg.arrival_airport} onChange={(e) => handleManualSegmentChange(idx, "arrival_airport", e.target.value.toUpperCase())} maxLength={3} className="uppercase font-mono" placeholder="JFK" /><p className="text-xs text-indigo-400 mt-0.5">{getAirportName(seg.arrival_airport)}</p></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Departure</Label><Input type="datetime-local" value={seg.departure_time} onChange={(e) => handleManualSegmentChange(idx, "departure_time", e.target.value)} /></div>
                    <div><Label className="text-xs">Arrival</Label><Input type="datetime-local" value={seg.arrival_time} onChange={(e) => handleManualSegmentChange(idx, "arrival_time", e.target.value)} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Flight Number</Label><Input value={seg.flight_number} onChange={(e) => handleManualSegmentChange(idx, "flight_number", e.target.value.toUpperCase())} className="uppercase font-mono" placeholder="BA123" /></div>
                    <div><Label className="text-xs">Operated By</Label><Input value={seg.operated_by} onChange={(e) => handleManualSegmentChange(idx, "operated_by", e.target.value)} placeholder="British Airways" /></div>
                  </div>
                </div>
              ))}
            </div>
            <div><Label>Notes</Label><Textarea value={manualDealForm.notes} onChange={(e) => setManualDealForm(p => ({ ...p, notes: e.target.value }))} placeholder="Any additional notes..." /></div>
            <Button onClick={handleAddManualDeal} className="w-full btn-primary">Add Deal</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ══════ Edit Manual Deal Dialog ══════ */}
      <Dialog open={editDealDialog} onOpenChange={setEditDealDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Manual Deal</DialogTitle></DialogHeader>
          {editDealForm && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Airline</Label><Input value={editDealForm.airline} onChange={(e) => setEditDealForm(p => ({ ...p, airline: e.target.value }))} placeholder="e.g., British Airways" /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Price</Label><Input type="number" value={editDealForm.price} onChange={(e) => setEditDealForm(p => ({ ...p, price: e.target.value }))} placeholder="0.00" /></div>
                  <div><Label>Currency</Label>
                    <Select value={editDealForm.currency} onValueChange={(v) => setEditDealForm(p => ({ ...p, currency: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="GBP">GBP</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="AUD">AUD</SelectItem>
                        <SelectItem value="AED">AED</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Class</Label>
                    <Select value={editDealForm.cabin_class} onValueChange={(v) => setEditDealForm(p => ({ ...p, cabin_class: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="economy">Economy</SelectItem>
                        <SelectItem value="business">Business</SelectItem>
                        <SelectItem value="first">First</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div><Label>Baggage</Label><Input value={editDealForm.baggage_included} onChange={(e) => setEditDealForm(p => ({ ...p, baggage_included: e.target.value }))} placeholder="e.g. 1x 23kg checked + 7kg cabin" /></div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Flight Segments ({editDealForm.segments.length})</Label>
                  <Button variant="ghost" size="sm" onClick={addEditSegment} disabled={editDealForm.segments.length >= 6} className="text-blue-600">+ Add Segment</Button>
                </div>
                {editDealForm.segments.map((seg, idx) => (
                  <div key={idx} className="p-4 bg-blue-50 rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-blue-700">Segment {idx + 1}</span>
                      {editDealForm.segments.length > 1 && <Button variant="ghost" size="sm" onClick={() => removeEditSegment(idx)}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-xs">From</Label><Input value={seg.departure_airport} onChange={(e) => handleEditSegmentChange(idx, "departure_airport", e.target.value.toUpperCase())} maxLength={3} className="uppercase font-mono" placeholder="LHR" /><p className="text-xs text-blue-400 mt-0.5">{getAirportName(seg.departure_airport)}</p></div>
                      <div><Label className="text-xs">To</Label><Input value={seg.arrival_airport} onChange={(e) => handleEditSegmentChange(idx, "arrival_airport", e.target.value.toUpperCase())} maxLength={3} className="uppercase font-mono" placeholder="JFK" /><p className="text-xs text-blue-400 mt-0.5">{getAirportName(seg.arrival_airport)}</p></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-xs">Departure</Label><Input type="datetime-local" value={seg.departure_time} onChange={(e) => handleEditSegmentChange(idx, "departure_time", e.target.value)} /></div>
                      <div><Label className="text-xs">Arrival</Label><Input type="datetime-local" value={seg.arrival_time} onChange={(e) => handleEditSegmentChange(idx, "arrival_time", e.target.value)} /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-xs">Flight Number</Label><Input value={seg.flight_number} onChange={(e) => handleEditSegmentChange(idx, "flight_number", e.target.value.toUpperCase())} className="uppercase font-mono" placeholder="BA123" /></div>
                      <div><Label className="text-xs">Operated By</Label><Input value={seg.operated_by} onChange={(e) => handleEditSegmentChange(idx, "operated_by", e.target.value)} placeholder="British Airways" /></div>
                    </div>
                  </div>
                ))}
              </div>
              <div><Label>Notes</Label><Textarea value={editDealForm.notes} onChange={(e) => setEditDealForm(p => ({ ...p, notes: e.target.value }))} placeholder="Any additional notes..." /></div>
              <Button onClick={handleSaveEditDeal} className="w-full btn-primary">Save Changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ══════ Booking Dialog ══════ */}
      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create Booking</DialogTitle></DialogHeader>
          <div className="space-y-6 mt-4">
            <Card className="bg-slate-50">
              <CardHeader><CardTitle className="text-lg flex items-center gap-2"><DollarSign className="w-5 h-5" />Pricing & Profit</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label>Base Price (EUR)</Label><Input type="number" value={bookingForm.base_price} onChange={(e) => setBookingForm(p => ({ ...p, base_price: e.target.value }))} className="font-mono" /></div>
                  <div><Label>Selling Price (EUR)</Label><Input type="number" value={bookingForm.selling_price} onChange={(e) => setBookingForm(p => ({ ...p, selling_price: e.target.value }))} className="font-mono" /></div>
                  <div><Label>Profit (EUR)</Label><div className={`h-10 flex items-center px-3 rounded-md border font-mono font-bold ${parseFloat(profit) >= 0 ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-red-50 border-red-200 text-red-700"}`}>€{profit}</div></div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Users className="w-5 h-5" />Passengers ({bookingForm.passengers.length})</CardTitle></CardHeader>
              <CardContent className="max-h-[400px] overflow-y-auto">
                {bookingForm.passengers.map((passenger, index) => (
                  <div key={index} className={`space-y-4 p-4 rounded-lg mb-4 ${passenger.passenger_type === "infant" ? "bg-amber-50 border border-amber-200" : passenger.passenger_type === "child" ? "bg-blue-50 border border-blue-200" : "bg-slate-50"}`}>
                    <div className="flex items-center gap-2"><p className="font-medium">Passenger {index + 1}</p><Badge variant="outline" className="text-xs capitalize">{passenger.passenger_type || "adult"}</Badge></div>
                    <div className="grid grid-cols-4 gap-4">
                      <div><Label>Title</Label>
                        <Select value={passenger.title} onValueChange={(v) => { const p = [...bookingForm.passengers]; p[index].title = v; p[index].gender = ["Mrs","Ms","Miss"].includes(v) ? "f" : "m"; setBookingForm(prev => ({ ...prev, passengers: p })); }}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="Mr">Mr</SelectItem><SelectItem value="Mrs">Mrs</SelectItem><SelectItem value="Ms">Ms</SelectItem><SelectItem value="Miss">Miss</SelectItem><SelectItem value="Mstr">Mstr</SelectItem><SelectItem value="Dr">Dr</SelectItem></SelectContent>
                        </Select>
                      </div>
                      <div><Label>First Name</Label><Input value={passenger.first_name} onChange={(e) => { const p = [...bookingForm.passengers]; p[index].first_name = e.target.value; setBookingForm(prev => ({ ...prev, passengers: p })); }} /></div>
                      <div><Label>Last Name</Label><Input value={passenger.last_name} onChange={(e) => { const p = [...bookingForm.passengers]; p[index].last_name = e.target.value; setBookingForm(prev => ({ ...prev, passengers: p })); }} /></div>
                      <div><Label>Date of Birth</Label><Input type="date" value={passenger.date_of_birth} onChange={(e) => { const p = [...bookingForm.passengers]; p[index].date_of_birth = e.target.value; setBookingForm(prev => ({ ...prev, passengers: p })); }} /></div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Button onClick={handleCreateBooking} className="w-full btn-accent h-12">
              <CheckCircle className="w-4 h-4 mr-2" />Confirm Booking
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ══════ Edit Request Dialog ══════ */}
      <Dialog open={showEditRequest} onOpenChange={setShowEditRequest}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil className="w-4 h-4" />Edit Flight Request</DialogTitle></DialogHeader>
          {editForm && (
            <div className="space-y-5 mt-2">
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs">Travel Type</Label>
                  <Select value={editForm.travel_type} onValueChange={v => setEditForm(p => ({ ...p, travel_type: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="one_way">One Way</SelectItem><SelectItem value="return">Return</SelectItem><SelectItem value="multi_city">Multi City</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Class</Label>
                  <Select value={editForm.travel_class} onValueChange={v => setEditForm(p => ({ ...p, travel_class: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="economy">Economy</SelectItem><SelectItem value="premium_economy">Premium Economy</SelectItem><SelectItem value="business">Business</SelectItem><SelectItem value="first">First</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Baggage</Label>
                  <Select value={editForm.baggage} onValueChange={v => setEditForm(p => ({ ...p, baggage: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="no_baggage">No Baggage</SelectItem><SelectItem value="hand_carry_only">Hand Carry Only</SelectItem><SelectItem value="23kg">23kg</SelectItem><SelectItem value="2x23kg">2x23kg</SelectItem><SelectItem value="30kg">30kg</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold">Passengers</Label>
                <div className="grid grid-cols-3 gap-3 mt-1.5">
                  {[["adults","Adults"],["children","Children (2-11)"],["infants","Infants (<2)"]].map(([key, label]) => (
                    <div key={key}><Label className="text-xs text-gray-500">{label}</Label><Input type="number" min={key === "adults" ? 1 : 0} max={9} value={editForm.passengers[key] || 0} onChange={e => setEditForm(p => ({ ...p, passengers: { ...p.passengers, [key]: parseInt(e.target.value) || 0 } }))} className="mt-1" /></div>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2"><Label className="text-xs font-semibold">Flight Segments</Label><button onClick={() => setEditForm(p => ({ ...p, segments: [...p.segments, { departure_airport: "", arrival_airport: "", departure_date: "" }] }))} className="text-xs text-indigo-600 hover:underline">+ Add segment</button></div>
                {editForm.segments.map((seg, i) => (
                  <div key={i} className="p-3 bg-indigo-50 rounded-lg mb-2 space-y-2">
                    <div className="flex items-center justify-between"><span className="text-xs font-medium text-indigo-700">Segment {i + 1}</span>{editForm.segments.length > 1 && <button onClick={() => setEditForm(p => ({ ...p, segments: p.segments.filter((_, j) => j !== i) }))} className="text-red-400 hover:text-red-600 text-xs">Remove</button>}</div>
                    <div className="grid grid-cols-3 gap-2">
                      <div><Label className="text-xs">From</Label><Input value={seg.departure_airport} onChange={e => { const segs = [...editForm.segments]; segs[i] = { ...segs[i], departure_airport: e.target.value.toUpperCase() }; setEditForm(p => ({ ...p, segments: segs })); }} maxLength={3} className="uppercase font-mono text-center" placeholder="AMS" /><p className="text-[10px] text-indigo-400 mt-0.5">{getAirportName(seg.departure_airport)}</p></div>
                      <div><Label className="text-xs">To</Label><Input value={seg.arrival_airport} onChange={e => { const segs = [...editForm.segments]; segs[i] = { ...segs[i], arrival_airport: e.target.value.toUpperCase() }; setEditForm(p => ({ ...p, segments: segs })); }} maxLength={3} className="uppercase font-mono text-center" placeholder="KHI" /><p className="text-[10px] text-indigo-400 mt-0.5">{getAirportName(seg.arrival_airport)}</p></div>
                      <div><Label className="text-xs">Date</Label><Input type="date" value={seg.departure_date} onChange={e => { const segs = [...editForm.segments]; segs[i] = { ...segs[i], departure_date: e.target.value }; setEditForm(p => ({ ...p, segments: segs })); }} /></div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <Button onClick={handleSaveRequest} disabled={savingEdit} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white">{savingEdit ? "Saving..." : "Save Changes"}</Button>
                <Button variant="outline" onClick={() => setShowEditRequest(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ══════ Send Quote Dialog ══════ */}
      <Dialog open={showQuoteDialog} onOpenChange={setShowQuoteDialog}>
        <DialogContent className="max-w-md flex flex-col" style={{maxHeight: "85vh"}}>
          <DialogHeader className="shrink-0 pb-2 border-b">
            <DialogTitle className="flex items-center gap-2 text-base">
              <FileText className="w-4 h-4 text-indigo-600" /> Send Quote
            </DialogTitle>
            {request && (
              <p className="text-xs text-slate-500 mt-1">
                To: <strong>{request.first_name} {request.last_name}</strong> &nbsp;·&nbsp; {request.email}
                {quoteOffer && <span> &nbsp;·&nbsp; {quoteOffer.type === "duffel" ? quoteOffer.owner?.name : quoteOffer.airline}</span>}
              </p>
            )}
          </DialogHeader>
          <div className="overflow-y-auto flex-1 space-y-3 py-3 pr-1">
            <div>
              <Label className="text-xs text-slate-500">Send to email</Label>
              <Input value={sendToEmail} onChange={e => setSendToEmail(e.target.value)} placeholder="customer@email.com" className="mt-1 h-9 text-sm" />
            </div>
            {quoteOffer?.itinerary?.length > 0 && (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide px-3 py-1.5 bg-slate-50 border-b">Itinerary in email</p>
                <div className="divide-y divide-slate-100">
                  {quoteOffer.itinerary.map((seg, i) => (
                    <div key={i} className="px-3 py-2 flex items-center gap-2">
                      <span className="text-[10px] font-bold text-indigo-600 w-4 shrink-0">{i+1}</span>
                      <span className="font-mono font-bold text-slate-800 text-sm">{seg.departure_airport}</span>
                      <span className="text-slate-400 text-xs">→</span>
                      <span className="font-mono font-bold text-slate-800 text-sm">{seg.arrival_airport}</span>
                      {seg.flight_number && <span className="text-[10px] text-slate-400 font-mono">{seg.flight_number}</span>}
                      <span className="text-[10px] text-slate-400 ml-auto">{seg.departure_date}{seg.departure_time && ` · ${seg.departure_time}`}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-slate-500">Your Cost (€) — hidden</Label>
                <Input type="number" step="0.01" value={quoteForm.base_price} onChange={e => setQuoteForm(p => ({ ...p, base_price: e.target.value }))} className="mt-1 h-9 text-sm" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Quote Price (€) *</Label>
                <Input type="number" step="0.01" value={quoteForm.selling_price} onChange={e => setQuoteForm(p => ({ ...p, selling_price: e.target.value }))} placeholder="0.00" className="mt-1 h-9 text-sm" autoFocus />
              </div>
            </div>
            {quoteForm.selling_price && quoteForm.base_price && (
              <div className={`py-1.5 rounded text-xs text-center font-semibold ${parseFloat(quoteForm.selling_price) - parseFloat(quoteForm.base_price) >= 0 ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
                Profit: €{(parseFloat(quoteForm.selling_price) - parseFloat(quoteForm.base_price)).toFixed(2)}
              </div>
            )}
            <div>
              <Label className="text-xs text-slate-500">Baggage Allowance</Label>
              <Input value={quoteForm.baggage_allowance} onChange={e => setQuoteForm(p => ({ ...p, baggage_allowance: e.target.value }))} placeholder="e.g. 1x 23kg checked + 1x 7kg cabin" className="mt-1 h-9 text-sm" />
            </div>
            <div className="pt-3 border-t border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold text-slate-700">Research Effort (shown to customer)</Label>
                <label className="flex items-center gap-2 text-xs text-slate-500">
                  <input type="checkbox" checked={quoteForm.include_research_summary} onChange={e => setQuoteForm(p => ({ ...p, include_research_summary: e.target.checked }))} className="rounded border-slate-300" />
                  Include
                </label>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-[10px] text-slate-400">Dates Searched</Label><Input type="number" value={quoteForm.dates_searched} onChange={e => setQuoteForm(p => ({ ...p, dates_searched: e.target.value }))} placeholder="14" className="mt-1 h-8 text-sm" /></div>
                <div><Label className="text-[10px] text-slate-400">Airports Checked</Label><Input type="number" value={quoteForm.airports_searched} onChange={e => setQuoteForm(p => ({ ...p, airports_searched: e.target.value }))} placeholder="6" className="mt-1 h-8 text-sm" /></div>
                <div><Label className="text-[10px] text-slate-400">Hours Spent</Label><Input type="number" step="0.5" value={quoteForm.hours_spent} onChange={e => setQuoteForm(p => ({ ...p, hours_spent: e.target.value }))} placeholder="2" className="mt-1 h-8 text-sm" /></div>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Internal note (not sent)</Label>
              <Input value={quoteForm.notes} onChange={e => setQuoteForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional" className="mt-1 h-9 text-sm" />
            </div>
          </div>
          <div className="flex gap-2 shrink-0 pt-3 border-t">
            <Button onClick={handleSendQuote} disabled={sendingQuote} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white">
              {sendingQuote ? "Sending..." : `Send Quote → ${sendToEmail || request?.email}`}
            </Button>
            <Button variant="outline" onClick={() => setShowQuoteDialog(false)}>Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
